/**
 * The measuring wizard, as data.
 *
 * One entry per step, in the order of the pro shop aide-mémoire: warm the
 * hand, middle finger, flexibility, holes. Each step names the fields it is
 * about -- all of them fields the sheet already has and ModalSpecsheets
 * already knows how to edit -- and carries the technique text that until now
 * was only on paper.
 *
 * `kind` is what the step needs beyond a row of values: a calculator, a set
 * of switches, a summary. Everything else is plain fields.
 */

/**
 * Hidden for now, at Mark's request: the header button, the offer on a bowler
 * with no sheet, and the setting that controls the offer. Flip it back and all
 * three return; nothing else was taken out.
 */
export const WIZARD_ENABLED = false;

export const GRIP_CONVENTIONAL = "conventional";
export const GRIP_FINGERTIP = "finger_tip";

/** A translation key with the English it falls back to. */
const copy = (key, text) => ({ key, text });

/** t() for the pairs above, so a missing key still reads as a sentence. */
export const tr = (t, entry) => (entry ? t(entry.key, entry.text) : "");

const field = (name, key, text, options = {}) => ({
  name,
  label: copy(key, text),
  ...options,
});

export const STEPS = [
  {
    id: "identity",
    kind: "identity",
    grips: "all",
    title: copy("wizard.step.identity.title", "Who they are, and their hand"),
    coach: copy(
      "wizard.step.identity.coach",
      "Check the hand before measuring anything: arthritis, tendinitis, calluses, swelling or a missing finger all change the grip. For weight, a house ball that stays comfortable for one game is the right weight; comfortable for three games means 2 lb more. Let them try it before you drill."
    ),
    checks: [
      copy(
        "wizard.step.identity.check.hand",
        "Hand checked: arthritis, tendinitis, calluses, swelling, missing finger"
      ),
      copy(
        "wizard.step.identity.check.weight",
        "Weight tried with a house ball before drilling"
      ),
    ],
    fields: [],
  },
  {
    id: "grip",
    kind: "grip",
    grips: "all",
    title: copy("wizard.step.grip.title", "Warm the hand, choose the grip"),
    coach: copy(
      "wizard.step.grip.coach",
      "Massage the hand and have them bowl a game first — a cold hand measures short. Then choose the grip: everything after this follows it."
    ),
    checks: [copy("wizard.step.grip.check.warm", "Hand warmed up")],
    fields: [field("ball_grip", "wizard.field.ball_grip", "Grip")],
  },
  {
    id: "middle",
    kind: "middle",
    grips: "all",
    title: copy("wizard.step.middle.title", "Measure the middle finger"),
    coach: {
      [GRIP_CONVENTIONAL]: copy(
        "wizard.step.middle.coach.conventional",
        "Mark the crease of the SECOND joint, draw a line 3/8 in below it, and measure from that line."
      ),
      [GRIP_FINGERTIP]: copy(
        "wizard.step.middle.coach.fingertip",
        "Mark the 1st and the 2nd joint, measure the distance between them, draw a line halfway, and measure from that line with the fitting tool."
      ),
    },
    fields: [
      field("f1_span_full", "wizard.field.f1_span_full", "Middle finger span"),
      field("f1_span_cut", "wizard.field.f1_span_cut", "Cut to cut"),
    ],
  },
  {
    id: "ring",
    kind: "ring",
    grips: "all",
    title: copy("wizard.step.ring.title", "Measure the ring finger"),
    coach: {
      [GRIP_CONVENTIONAL]: copy(
        "wizard.step.ring.coach.conventional",
        "The ring finger is always 1/8 in longer than the middle finger span."
      ),
      [GRIP_FINGERTIP]: copy(
        "wizard.step.ring.coach.fingertip",
        "Mark the ring finger's 1st joint with the hand slightly cupped, then carry the middle finger's line across. X is the gap between the two marks, and the ring finger's difference is 5/16 − X. It is the rule that gets forgotten most."
      ),
    },
    fields: [
      field("f2_span_full", "wizard.field.f2_span_full", "Ring finger span"),
      field("f2_span_cut", "wizard.field.f2_span_cut", "Cut to cut"),
    ],
  },
  {
    id: "fingerFlex",
    kind: "fingerFlex",
    grips: [GRIP_FINGERTIP],
    title: copy("wizard.step.fingerFlex.title", "Finger flexibility"),
    coach: copy(
      "wizard.step.fingerFlex.coach",
      "Read the flexibility of the first joint. 70° supple takes zero pitch with the right span, 45° takes 1/4 to 3/8 reverse, 30° takes 1/2 to 3/4 reverse. More than 80% of customers get reverse pitch only — drill what you see."
    ),
    fields: [
      field("f1_rev", "wizard.field.f1_rev", "Middle finger reverse"),
      field("f2_rev", "wizard.field.f2_rev", "Ring finger reverse"),
      field("f1_for", "wizard.field.f1_for", "Middle finger forward"),
      field("f2_for", "wizard.field.f2_for", "Ring finger forward"),
    ],
  },
  {
    id: "inserts",
    kind: "inserts",
    grips: "all",
    title: copy("wizard.step.inserts.title", "Inserts and outer angle"),
    coach: copy(
      "wizard.step.inserts.coach",
      "The pitch depends on the insert. Oval and Perfect Oval need nothing; Power Oval adds 1/8, Semi adds 3/16, and Power Regular, PowerLift and Power Flat add 1/4 of outer angle."
    ),
    fields: [
      field("f1_grip_type", "wizard.field.f1_grip_type", "Middle finger insert"),
      field("f2_grip_type", "wizard.field.f2_grip_type", "Ring finger insert"),
    ],
  },
  {
    id: "constants",
    kind: "fields",
    grips: "all",
    title: copy("wizard.step.constants.title", "The three constants"),
    coach: {
      [GRIP_CONVENTIONAL]: copy(
        "wizard.step.constants.coach.conventional",
        "Bridge 3/8. Laterals 1/4 on the middle finger and 5/8 on the ring finger."
      ),
      [GRIP_FINGERTIP]: copy(
        "wizard.step.constants.coach.fingertip",
        "Bridge 1/4. Laterals 3/8 on the middle finger and 1/2 on the ring finger."
      ),
    },
    fields: [
      field("bridge", "wizard.field.bridge", "Bridge", { auto: true }),
      field("f1_lat", "wizard.field.f1_lat", "Middle finger lateral", {
        auto: true,
      }),
      field("f2_lat", "wizard.field.f2_lat", "Ring finger lateral", {
        auto: true,
      }),
      field("clt", "wizard.field.clt", "CLT"),
    ],
  },
  {
    id: "thumbFlex",
    kind: "thumb",
    grips: "all",
    title: copy("wizard.step.thumbFlex.title", "Thumb flexibility"),
    coach: {
      [GRIP_CONVENTIONAL]: copy(
        "wizard.step.thumbFlex.coach.conventional",
        "Pull the thumb back and read the flexibility; the pitch starts from the table. A conventional grip uses the same pitches at the thumb and at the fingers."
      ),
      [GRIP_FINGERTIP]: copy(
        "wizard.step.thumbFlex.coach.fingertip",
        "Pull the thumb back and read the flexibility; the pitch starts from the table. Excessive grip pressure? Check the holes are not too big."
      ),
    },
    fields: [
      field("thm_flex", "wizard.field.thm_flex", "Thumb flexibility"),
      field("thm_for", "wizard.field.thm_for", "Thumb forward", { auto: true }),
      field("thm_rev", "wizard.field.thm_rev", "Thumb reverse", { auto: true }),
      field("thm_lat_l", "wizard.field.thm_lat_l", "Thumb lateral left"),
      field("thm_lat_r", "wizard.field.thm_lat_r", "Thumb lateral right"),
    ],
  },
  {
    id: "thumbHole",
    kind: "fields",
    grips: "all",
    title: copy("wizard.step.thumbHole.title", "Thumb type and insert"),
    coach: copy(
      "wizard.step.thumbHole.coach",
      "Thumb type, insert and taper. If the thumb is oval, close the wizard for a moment and use Calculate Oval on the sheet — the wizard reopens on this step."
    ),
    fields: [
      field("thm_type", "wizard.field.thm_type", "Thumb type"),
      field("thm_insert_type", "wizard.field.thm_insert_type", "Thumb insert"),
      field("thm_round_taper", "wizard.field.thm_round_taper", "Taper"),
      field("thm_offset", "wizard.field.thm_offset", "Offset"),
    ],
  },
  {
    id: "holes",
    kind: "holes",
    grips: "all",
    title: copy("wizard.step.holes.title", "Hole sizes"),
    coach: {
      [GRIP_CONVENTIONAL]: copy(
        "wizard.step.holes.coach.conventional",
        "The finger goes in easily as far as the second joint, without including it. Not sure? Go down 1/64 — you can always open it up after drilling."
      ),
      [GRIP_FINGERTIP]: copy(
        "wizard.step.holes.coach.fingertip",
        "The finger goes in easily as far as the second joint, without including it. Allow for oval shapes and taper. Not sure? Go down 1/64 — you can always open it up after drilling."
      ),
    },
    fields: [
      field("f1_size", "wizard.field.f1_size", "Middle finger size"),
      field("f2_size", "wizard.field.f2_size", "Ring finger size"),
      field("f1_od", "wizard.field.f1_od", "Middle finger OD", { auto: true }),
      field("f2_od", "wizard.field.f2_od", "Ring finger OD", { auto: true }),
      field("thm_size", "wizard.field.thm_size", "Thumb size", { thumb: true }),
      field("thm_od", "wizard.field.thm_od", "Thumb OD", {
        auto: true,
        thumb: true,
      }),
    ],
  },
  {
    id: "review",
    kind: "review",
    grips: "all",
    title: copy("wizard.step.review.title", "Review and finish"),
    coach: copy(
      "wizard.step.review.coach",
      "Anything left empty is listed below. Tap it to go back to its step."
    ),
    fields: [],
  },
];

/** The warning each grip ends on. They are opposites, so only one can show. */
export const GRIP_WARNINGS = {
  [GRIP_CONVENTIONAL]: copy(
    "wizard.warning.conventional",
    "A conventional grip should NEVER need a wrist brace."
  ),
  [GRIP_FINGERTIP]: copy(
    "wizard.warning.fingertip",
    "Wrist brace: measure with it on, in the position it is normally worn. Re-drill if the player changes grip type."
  ),
};

/**
 * Complete enough to start measuring.
 *
 * The same rule the bowler header uses to decide whether to open the profile
 * editor by itself: a name always, email and phone only where the shop asks
 * for them. Anything the wizard does before this is answered lands on top of
 * that editor.
 */
export const profileIsComplete = (bowler, { requireEmail, requirePhone } = {}) =>
  !!bowler &&
  !!bowler.firstname &&
  !!bowler.lastname &&
  (requireEmail !== true || !!bowler.email) &&
  (requirePhone !== true || !!bowler.telephone);

/**
 * The steps this bowler actually walks through.
 *
 * A two-hander has no thumb, so the thumb steps are dropped rather than shown
 * empty; a conventional grip with no inserts skips the insert step; and the
 * identity step only appears for someone who has just been created.
 */
export const visibleSteps = ({
  grip,
  is2Hander = false,
  hasFingerInserts = false,
  needsIdentity = false,
}) =>
  STEPS.filter((step) => {
    if (step.grips !== "all" && !step.grips.includes(grip)) return false;
    if (step.id === "identity" && !needsIdentity) return false;
    if (is2Hander && (step.id === "thumbFlex" || step.id === "thumbHole")) {
      return false;
    }
    if (
      step.id === "inserts" &&
      grip === GRIP_CONVENTIONAL &&
      !hasFingerInserts
    ) {
      return false;
    }

    return true;
  });

/** The fields of a step that this bowler is actually asked for. */
export const stepFields = (step, { is2Hander = false } = {}) =>
  (step.fields || []).filter((entry) => !(entry.thumb && is2Hander));

/** The technique text for a step, which for most steps depends on the grip. */
export const stepCoach = (step, grip) =>
  step.coach && step.coach.key ? step.coach : step.coach?.[grip];
